// src/pages/notifications/StudentNotifications.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BellIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import apiClient from "../../api/client";
import { useAuth } from "../../context/AuthContext";

const TERM_LABELS = {
  term_1: "Term 1",
  term_2: "Term 2",
  term_3: "Term 3",
};

const getIcon = (type) => {
  const icons = {
    grade: { icon: AcademicCapIcon, color: "text-blue-500", bg: "bg-blue-50" },
    class: {
      icon: CalendarDaysIcon,
      color: "text-purple-500",
      bg: "bg-purple-50",
    },
    attendance: {
      icon: CheckCircleIcon,
      color: "text-green-500",
      bg: "bg-green-50",
    },
    subject: {
      icon: BookOpenIcon,
      color: "text-orange-500",
      bg: "bg-orange-50",
    },
    enrollment: {
      icon: AcademicCapIcon,
      color: "text-indigo-500",
      bg: "bg-indigo-50",
    },
    teacher: {
      icon: UserGroupIcon,
      color: "text-cyan-600",
      bg: "bg-cyan-50",
    },
  };

  const selected = icons[type] || {
    icon: BellIcon,
    color: "text-gray-500",
    bg: "bg-gray-50",
  };
  const IconComponent = selected.icon;

  return (
    <div
      className={`w-12 h-12 rounded-full ${selected.bg} flex items-center justify-center flex-shrink-0`}
    >
      <IconComponent className={`w-6 h-6 ${selected.color}`} />
    </div>
  );
};

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const timeAgo = (value) => {
  const date = toDate(value);
  if (!date) return "Current record";

  const diffMs = Math.max(0, Date.now() - date.getTime());
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const exactDate = (value) => {
  const date = toDate(value);
  if (!date) return "Date unavailable";
  return date.toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const dateGroup = (value) => {
  const date = toDate(value);
  if (!date) return "Earlier records";

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const daysAgo = Math.round((startOfToday - startOfDate) / 86400000);

  if (daysAgo === 0) return "Today";
  if (daysAgo === 1) return "Yesterday";
  if (daysAgo < 7) return "This week";
  return date.toLocaleDateString([], { month: "long", year: "numeric" });
};

const TYPE_LABELS = {
  grade: "Grade",
  attendance: "Attendance",
  subject: "Subject",
  enrollment: "Enrolment",
  class: "Class",
  teacher: "Teacher",
};

const ASSESSMENT_FIELDS = [
  { key: "test1", label: "Test 1" },
  { key: "test2", label: "Test 2" },
  { key: "exam", label: "Exam" },
];

const getRecordedAssessments = (term) =>
  ASSESSMENT_FIELDS.flatMap(({ key, label }) => {
    const rawScore = term?.[key];
    const score = Number(rawScore);
    if (
      rawScore === null ||
      rawScore === undefined ||
      rawScore === "" ||
      !Number.isFinite(score) ||
      score <= 0
    ) {
      return [];
    }
    return [{ key, label, score }];
  });

const sortActivitiesNewestFirst = (items) =>
  [...items].sort((a, b) => {
    const aTime = toDate(a.createdAt)?.getTime() || 0;
    const bTime = toDate(b.createdAt)?.getTime() || 0;

    // Newest dated records always come first. Records without a valid date
    // remain at the bottom of the activity list.
    if (bTime !== aTime) return bTime - aTime;
    return String(b.id).localeCompare(String(a.id));
  });

const getResponseArray = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  const candidates = [
    payload.rows,
    payload.students,
    payload.teachers,
    payload.children,
    response?.data?.rows,
  ];
  return candidates.find(Array.isArray) || [];
};

const getPersonName = (person, fallback = "Student") =>
  person?.full_name ||
  person?.fullName ||
  [
    person?.first_name || person?.firstName,
    person?.last_name || person?.lastName,
  ]
    .filter(Boolean)
    .join(" ") ||
  fallback;

// A teacher/class assignment belongs to the TeacherClass join table. Never
// use the Class row's created_at/updated_at because those timestamps describe
// the class itself and can predate the teacher account.
const getTeacherClassAssignmentDate = (classItem) => {
  const assignment =
    classItem?.TeacherClass ||
    classItem?.teacherClass ||
    classItem?.teacher_class ||
    classItem?.TeacherClasses?.[0] ||
    classItem?.teacher_classes?.[0] ||
    null;

  return (
    assignment?.created_at ||
    assignment?.createdAt ||
    assignment?.updated_at ||
    assignment?.updatedAt ||
    classItem?.assignment_date ||
    classItem?.assignmentDate ||
    null
  );
};

const buildGradeActivities = (gradeRecords, ownerName = "") => {
  const activities = [];

  gradeRecords.forEach((grade, gradeIndex) => {
    const subjectName = grade.subject || grade.subject_name || "Subject";
    const subjectId = grade.subject_id || grade.subjectId || gradeIndex;
    const terms = grade.terms || {};

    Object.entries(TERM_LABELS).forEach(([termKey, termLabel]) => {
      const term = terms[termKey];
      const recordedAssessments = getRecordedAssessments(term);

      recordedAssessments.forEach(({ key, label, score }) => {
        activities.push({
          id: `grade-${subjectId}-${termKey}-${key}`,
          title: `${label} Grade Posted`,
          message: `${ownerName ? `${ownerName}: ` : ""}${subjectName} ${label} for ${termLabel}: ${score}%`,
          type: "grade",
          createdAt:
            term.updated_at ||
            term.updatedAt ||
            term.created_at ||
            term.createdAt ||
            grade.updated_at ||
            grade.updatedAt ||
            null,
        });
      });
    });
  });

  return activities;
};

const buildAttendanceActivities = (attendanceRecords, ownerName = "") =>
  attendanceRecords.map((record) => {
    const status = String(record.status || "recorded").toLowerCase();
    const session = record.session ? ` for the ${record.session} session` : "";
    const dateLabel = toDate(record.date)?.toLocaleDateString();

    return {
      id: `attendance-${record.id}`,
      title: "Attendance Recorded",
      message: `${ownerName ? `${ownerName} was` : "You were"} marked ${status}${session}${dateLabel ? ` on ${dateLabel}` : ""}.`,
      type: "attendance",
      // The attendance date is the real event date. Database timestamps only
      // describe when the row was inserted or edited.
      createdAt:
        record.date ||
        record.updated_at ||
        record.updatedAt ||
        record.created_at ||
        record.createdAt ||
        null,
    };
  });

const buildSubjectActivities = (subjectRecords) =>
  subjectRecords.map((subject, index) => ({
    id: `subject-${subject.id || index}`,
    title: "Assigned Subject",
    message: `${subject.name || "A subject"} is assigned to your class.`,
    type: "subject",
    createdAt:
      subject.updated_at ||
      subject.updatedAt ||
      subject.created_at ||
      subject.createdAt ||
      null,
  }));

export default function StudentNotifications() {
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const [activities, setActivities] = useState([]);
  const [readIds, setReadIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("all");

  const accountIdentity = user?.uuid || user?.id || user?.email;
  const readStorageKey = accountIdentity
    ? `pawaschool-${role || "user"}-activity-read:${accountIdentity}`
    : null;

  const pageContent = useMemo(() => {
    if (["admin", "school_admin", "super_admin"].includes(role)) {
      return {
        title: "School notifications",
        description:
          "Recent student enrolments and teacher additions in your school.",
        empty: "New school activity will appear here.",
      };
    }
    if (role === "parent") {
      return {
        title: "Children activity",
        description:
          "Recent grades, attendance and school records for your children.",
        empty: "New activity for your children will appear here.",
      };
    }
    if (role === "teacher") {
      return {
        title: "Teacher notifications",
        description: "Your latest class, subject and teaching assignments.",
        empty: "New class and subject assignments will appear here.",
      };
    }
    return {
      title: "Student activity",
      description:
        "Your latest grades, attendance, subjects and enrolment records.",
      empty: "New grades, attendance and subject records will appear here.",
    };
  }, [role]);

  useEffect(() => {
    if (!readStorageKey) return;

    try {
      const saved = JSON.parse(localStorage.getItem(readStorageKey) || "[]");
      setReadIds(new Set(Array.isArray(saved) ? saved : []));
    } catch {
      setReadIds(new Set());
    }
  }, [readStorageKey]);

  const persistReadIds = useCallback(
    (nextIds) => {
      setReadIds(nextIds);
      if (!readStorageKey) return;

      try {
        localStorage.setItem(readStorageKey, JSON.stringify([...nextIds]));
        window.dispatchEvent(new Event("pawaschool-notifications-updated"));
      } catch {
        // The activity feed still works if browser storage is unavailable.
      }
    },
    [readStorageKey],
  );

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (!user?.id) {
        throw new Error("Your signed-in user information is unavailable.");
      }

      let realActivities = [];

      if (role === "teacher") {
        const teacherResponse = await apiClient.get("/teachers/me");
        const teacher = teacherResponse?.data?.data;
        if (!teacher?.id) {
          throw new Error("No teacher profile was found for your account.");
        }

        const [classesResult, subjectsResult] = await Promise.allSettled([
          apiClient.get(`/teachers/${teacher.id}/classes`),
          apiClient.get(`/teachers/${teacher.id}/subjects`),
        ]);
        const classes =
          classesResult.status === "fulfilled"
            ? getResponseArray(classesResult.value)
            : [];
        const subjectAssignments =
          subjectsResult.status === "fulfilled"
            ? getResponseArray(subjectsResult.value)
            : [];

        realActivities = [
          ...classes.map((classItem, index) => {
            const isClassTeacher =
              Number(teacher.class_teacher_class_id) === Number(classItem.id) ||
              Number(teacher.current_class_id) === Number(classItem.id);
            return {
              id: `teacher-class-${classItem.id || index}`,
              title: isClassTeacher
                ? "Class Teacher Assignment"
                : "Class Assigned",
              message: isClassTeacher
                ? `You are the class teacher for ${classItem.name || "a class"}.`
                : `${classItem.name || "A class"} is assigned to you.`,
              type: "class",
              createdAt: getTeacherClassAssignmentDate(classItem),
            };
          }),
          ...subjectAssignments.map((assignment, index) => {
            const subject = assignment.Subject || assignment.subject || {};
            const assignedClass = assignment.Class || assignment.class || {};
            const subjectName =
              subject.name || assignment.subject_name || "A subject";
            const className = assignedClass.name || assignment.class_name || "";
            return {
              id: `teacher-subject-${assignment.id || `${subject.id || index}-${assignedClass.id || "class"}`}`,
              title: "Subject Assigned",
              message: `${subjectName} is assigned to you${className ? ` for ${className}` : ""}.`,
              type: "subject",
              createdAt:
                assignment.updated_at ||
                assignment.updatedAt ||
                assignment.created_at ||
                assignment.createdAt ||
                null,
            };
          }),
        ];

        // created_at is the real teacher-profile creation timestamp.
        // joining_date is intentionally excluded because it is date-only and
        // incorrectly displays as midnight (00:00).
        const profileCreatedAt = teacher.created_at || teacher.createdAt;
        if (profileCreatedAt) {
          realActivities.push({
            id: `teacher-joined-${teacher.id}`,
            title: "Teacher Profile Created",
            message: "Your teacher profile was added to the school.",
            type: "teacher",
            createdAt: profileCreatedAt,
          });
        }
      } else if (["admin", "school_admin", "super_admin"].includes(role)) {
        const [studentsResult, teachersResult] = await Promise.allSettled([
          apiClient.get("/students", { params: { page: 1, limit: 1000 } }),
          apiClient.get("/teachers", { params: { page: 1, limit: 1000 } }),
        ]);
        const students =
          studentsResult.status === "fulfilled"
            ? getResponseArray(studentsResult.value)
            : [];
        const teachers =
          teachersResult.status === "fulfilled"
            ? getResponseArray(teachersResult.value)
            : [];

        realActivities = [
          ...students.map((student, index) => ({
            id: `admin-enrollment-${student.id || index}`,
            title: "Student Enrolled",
            message: `${getPersonName(student)} was enrolled${student.class?.name ? ` in ${student.class.name}` : ""}.`,
            type: "enrollment",
            createdAt:
              student.enrollment_date ||
              student.created_at ||
              student.createdAt ||
              null,
          })),
          ...teachers.map((teacher, index) => ({
            id: `admin-teacher-${teacher.id || index}`,
            title: "Teacher Added",
            message: `${getPersonName(teacher, "A teacher")} was added to the school.`,
            type: "teacher",
            createdAt: teacher.created_at || teacher.createdAt || null,
          })),
        ];
      } else {
        let students = [];
        if (role === "parent") {
          const embeddedChildren = user?.children || user?.parent?.children;
          if (Array.isArray(embeddedChildren) && embeddedChildren.length) {
            students = embeddedChildren;
          } else {
            let childrenResponse;
            try {
              childrenResponse = await apiClient.get("/parents/me/children");
            } catch (primaryError) {
              childrenResponse = await apiClient.get("/parents/children");
            }
            students = getResponseArray(childrenResponse);
          }
          if (!students.length) {
            throw new Error("No children are linked to your parent account.");
          }
        } else {
          const profileResponse = await apiClient.get(
            `/students/by-user/${user.id}`,
          );
          const student = profileResponse.data?.data;
          if (!profileResponse.data?.success || !student) {
            throw new Error("No student profile was found for your account.");
          }
          students = [student];
        }

        const activityGroups = await Promise.all(
          students.map(async (student) => {
            const ownerName = role === "parent" ? getPersonName(student) : "";
            const [gradesResult, attendanceResult, subjectsResult] =
              await Promise.allSettled([
                apiClient.get(`/students/${student.id}/grades`),
                apiClient.get(`/students/${student.id}/attendance`),
                apiClient.get(`/students/${student.id}/subjects`),
              ]);
            const grades =
              gradesResult.status === "fulfilled"
                ? getResponseArray(gradesResult.value)
                : [];
            const attendance =
              attendanceResult.status === "fulfilled"
                ? getResponseArray(attendanceResult.value)
                : [];
            const subjects =
              subjectsResult.status === "fulfilled"
                ? getResponseArray(subjectsResult.value)
                : [];
            const items = [
              ...buildGradeActivities(grades, ownerName),
              ...buildAttendanceActivities(attendance, ownerName),
              ...(role === "student" ? buildSubjectActivities(subjects) : []),
            ];
            const enrollmentDate =
              student.enrollment_date ||
              student.created_at ||
              student.createdAt ||
              null;
            if (enrollmentDate) {
              items.push({
                id: `enrollment-${student.id}`,
                title: "Student Enrolled",
                message: `${ownerName || "You"} ${ownerName ? "was" : "were"} enrolled${student.class?.name ? ` in ${student.class.name}` : ""}.`,
                type: "enrollment",
                createdAt: enrollmentDate,
              });
            }
            return items;
          }),
        );
        realActivities = activityGroups.flat();
      }

      setActivities(sortActivitiesNewestFirst(realActivities));
    } catch (fetchError) {
      console.error("Failed to load student activities:", fetchError);
      setActivities([]);
      setError(
        fetchError.response?.data?.message ||
          fetchError.message ||
          "We could not load your activities.",
      );
    } finally {
      setLoading(false);
    }
  }, [role, user]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Opening this page counts as viewing the currently loaded notifications.
  // Mark them as read silently and notify the shared navbar badge immediately.
  // No toast is shown for this automatic read action.
  useEffect(() => {
    if (loading || !readStorageKey || activities.length === 0) return;

    const nextIds = new Set(readIds);
    let changed = false;

    activities.forEach((activity) => {
      if (!nextIds.has(activity.id)) {
        nextIds.add(activity.id);
        changed = true;
      }
    });

    if (changed) {
      persistReadIds(nextIds);
    }
  }, [activities, loading, persistReadIds, readIds, readStorageKey]);

  const notifications = useMemo(
    () =>
      sortActivitiesNewestFirst(
        activities.map((activity) => ({
          ...activity,
          read: readIds.has(activity.id),
        })),
      ),
    [activities, readIds],
  );

  const markAsRead = (id) => {
    const nextIds = new Set(readIds);
    nextIds.add(id);
    persistReadIds(nextIds);
    toast.success("Marked as read");
  };

  const markAllAsRead = () => {
    persistReadIds(new Set(activities.map((activity) => activity.id)));
    toast.success("All activities marked as read");
  };

  const unreadCount = notifications.filter(
    (notification) => !notification.read,
  ).length;
  const visibleNotifications = useMemo(
    () =>
      view === "unread"
        ? notifications.filter((notification) => !notification.read)
        : notifications,
    [notifications, view],
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-48 bg-gray-200 rounded-full" />
          <div className="space-y-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-24 bg-gray-100 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <section className="mb-6 overflow-hidden rounded-2xl bg-white">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex items-start gap-4">
            <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
              <BellIcon className="h-6 w-6" />
              {unreadCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                {pageContent.title}
              </h1>
              <p className="mt-1 text-sm leading-5 text-gray-500">
                {pageContent.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch sm:self-auto">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 sm:flex-none"
              >
                <CheckIcon className="w-4 h-4" />
                <span className="hidden xs:inline">Mark all read</span>
                <span className="xs:hidden">Read all</span>
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 bg-gray-50 px-5 py-3 sm:px-6">
          <button
            type="button"
            onClick={() => setView("all")}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
              view === "all"
                ? "bg-gray-200 text-gray-900"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            All {notifications.length}
          </button>
          <button
            type="button"
            onClick={() => setView("unread")}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
              view === "unread"
                ? "bg-gray-200 text-gray-900"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Unread {unreadCount}
          </button>
          <span className="ml-auto text-xs font-medium text-gray-400">
            Newest first
          </span>
        </div>
      </section>

      {error ? (
        <div className="rounded-2xl bg-white py-14 text-center">
          <ExclamationTriangleIcon className="w-14 h-14 text-red-300 mx-auto mb-3" />
          <p className="font-semibold text-gray-800">
            Activities could not be loaded
          </p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
          <button
            type="button"
            onClick={fetchActivities}
            className="mt-5 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      ) : notifications.length === 0 ? (
        <div className="rounded-2xl bg-white py-16 text-center">
          <BellIcon className="w-20 h-20 text-primary-200 mx-auto mb-4" />
          <p className="text-primary-500 text-lg font-semibold">
            No activities yet
          </p>
          <p className="text-sm text-gray-400 mt-1">{pageContent.empty}</p>
        </div>
      ) : visibleNotifications.length === 0 ? (
        <div className="rounded-2xl bg-white px-6 py-14 text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-emerald-400" />
          <h3 className="mt-3 font-semibold text-gray-900">
            You are all caught up
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no unread activities.
          </p>
        </div>
      ) : (
        <div>
          {visibleNotifications.map((notification, index) => {
            const currentGroup = dateGroup(notification.createdAt);
            const previousGroup =
              index > 0
                ? dateGroup(visibleNotifications[index - 1].createdAt)
                : null;
            const showGroup = currentGroup !== previousGroup;

            return (
              <React.Fragment key={notification.id}>
                {showGroup && (
                  <div className="mb-2 mt-6 flex items-center gap-3 first:mt-0">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      {currentGroup}
                    </h2>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                )}

                <article
                  className={`group relative mb-2 overflow-hidden rounded-xl p-4 transition-colors duration-200 hover:bg-gray-50 sm:p-5 ${
                    notification.read ? "bg-white" : "bg-primary-50/40"
                  }`}
                >
                  {!notification.read && (
                    <span className="absolute inset-y-0 left-0 w-1 bg-primary-500" />
                  )}
                  <div className="flex items-start gap-3.5 sm:gap-4">
                    {getIcon(notification.type)}

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900 sm:text-[15px]">
                              {notification.title}
                            </h3>
                            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                              {TYPE_LABELS[notification.type] || "Activity"}
                            </span>
                            {!notification.read && (
                              <span className="rounded-md bg-primary-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-700">
                                New
                              </span>
                            )}
                          </div>
                          <p className="mt-1.5 break-words text-sm leading-6 text-gray-600">
                            {notification.message}
                          </p>
                        </div>

                        <div className="flex-shrink-0 text-left sm:text-right">
                          <p className="text-xs font-semibold text-gray-600">
                            {timeAgo(notification.createdAt)}
                          </p>
                          <p className="mt-0.5 text-[11px] text-gray-400">
                            {exactDate(notification.createdAt)}
                          </p>
                        </div>
                      </div>

                      {!notification.read && (
                        <button
                          type="button"
                          onClick={() => markAsRead(notification.id)}
                          className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-primary-700 transition hover:bg-primary-50"
                        >
                          <CheckIcon className="h-3.5 w-3.5" /> Mark as read
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}